import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';

import { UnknownAction } from '@reduxjs/toolkit';
import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import * as actions from './taxesActions';
import type { TaxProps, TaxesStoreProps } from './taxesTypes';
import * as taxesTypes from './taxesTypes';

export const QcsSyncTaxesStoreFactory = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<
  TaxProps,
  TaxesStoreProps
>({
  resourceName: 'TAXES',
  isProcessing: false,
});

function reducer(
  state: QcsSyncResourceStore<TaxProps, TaxesStoreProps> = QcsSyncTaxesStoreFactory(),
  action: UnknownAction,
): typeof state {
  switch (true) {
    case actions.getTaxInputs.match(action):
      return state.set('isLoading', true);
    case actions.getTaxInputsSuccess.match(action):
      return resourceUpserters.upsertResources(
        state,
        (action.payload as any).merge({
          resources: action.payload.resources.map((resource) =>
            taxesTypes.mkTax({ clientId: resource.clientId, id: resource.id, input: resource }),
          ),
        }),
      );
    case actions.getTaxInputsFailure.match(action):
      return resourceUpserters.completeWithError(state, action.payload);

    case actions.getTaxCalculations.match(action):
      return state.set('isLoading', true);
    case actions.getTaxCalculationsSuccess.match(action):
      return resourceUpserters.upsertResources(state, action.payload);
    case actions.getTaxCalculationsFailure.match(action):
      return resourceUpserters.completeWithError(state, action.payload);

    case actions.createTaxInput.match(action):
      return resourceUpserters.upsertResource(
        state,
        taxesTypes.mkTax({ clientId: action.payload.clientId, input: action.payload }),
      );
    case actions.createTaxInputSuccess.match(action):
      return resourceUpserters.upsertResource(
        state,
        taxesTypes.mkTax({ clientId: action.payload.clientId, id: action.payload.id, input: action.payload }),
      );

    case actions.updateTaxInput.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo(state, {
        ...action,
        payload: taxesTypes.mkTax({
          clientId: action.payload.clientId,
          id: action.payload.id,
          input: action.payload,
        }),
      });
    case actions.updateTaxInputSuccess.match(action):
      return resourceUpserters.upsertResource(
        state,
        taxesTypes.mkTax({ clientId: action.payload.clientId, id: action.payload.id, input: action.payload }),
      );

    case actions.deleteTaxInput.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createTaxInput);
    case actions.deleteTaxInputSuccess.match(action):
      return resourceUpserters.upsertResource(
        state,
        taxesTypes.mkTax({
          clientId: action.payload.clientId,
          id: action.payload.id,
          isDeleted: true,
          input: action.payload,
        }),
      );

    case actions.performTaxCalcSuccess.match(action): {
      const { data } = action.payload as any;
      return resourceUpserters.upsertResource(
        state,
        taxesTypes.mkTax({
          modifiedAt: data?.resource?.modifiedAt,
          clientId: data?.resource?.input?.clientId,
          id: data?.resource?.input?.id,
          input: data?.resource?.input,
          output: data?.resource?.output,
        }),
      );
    }

    case actions.performTaxPlanRefresh.match(action):
      return state.merge({ isProcessing: true });

    case actions.performTaxPlanRefreshSuccess.match(action):
    case actions.performTaxPlanRefreshFailure.match(action):
      return state.merge({ isProcessing: false });

    case actions.createTaxInputFailure.match(action):
    case actions.updateTaxInputFailure.match(action):
    case actions.deleteTaxInputFailure.match(action):
      return resourceUpserters.resetResources(state, action.payload);

    default:
      return state;
  }
}

export default reducer;
